import React, { useCallback, useMemo, useState } from "react";
import { getClassName } from "@utils";
import { CheckedIcon, ChevronDownIcon } from "@taskpane/icons";
import { Dropdown } from "../Dropdown";
import css from "./Select.module.scss";

export interface SelectOption<T> {
  value: T;
  label: string;
  dropdownLabel?: string;
  render?: (
    props: {
      isSelected: boolean;
      handleChange: (value: T, shouldClose: boolean) => void;
    } & Omit<SelectOption<T>, "render">
  ) => React.ReactNode;
}

export interface SelectProps<T> {
  disabled?: boolean;
  dropdownWidth?: number | string;
  icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  options?: SelectOption<T>[];
  placeholder?: string;
  selectedValue?: T | null;
  onChange?: (value: T) => void;
  renderContent?: (
    props: {
      handleChange: (value: T, shouldClose: boolean) => void;
    } & Pick<SelectProps<T>, "options" | "selectedValue">
  ) => React.ReactNode;
}

const Select = <T extends unknown>({
  disabled = false,
  dropdownWidth,
  icon: IconComponent,
  options = [],
  placeholder = "Select",
  selectedValue = null,
  onChange,
  renderContent,
}: SelectProps<T>): React.JSX.Element => {
  const [isDropdownOpened, setIsDropdownOpened] = useState(false);

  const selectedOption = useMemo(
    () => options.find((option) => option.value === selectedValue),
    [selectedValue, options]
  );

  const handleChange = useCallback(
    (value: T, shouldClose = true) => {
      if (onChange) {
        onChange(value);
      }

      if (shouldClose) {
        setIsDropdownOpened(false);
      }
    },
    [onChange]
  );

  const renderOption = (
    { value, label, dropdownLabel, render }: SelectOption<T>,
    index: number
  ) => {
    const isSelected = value === selectedValue;
    const attrs = {
      option: {
        type: "button" as const,
        className: css.option,
        disabled: isSelected,
        onClick: () => {
          handleChange(value);
        },
      },
    };

    if (render) {
      return render({
        value,
        label,
        dropdownLabel,
        isSelected,
        handleChange,
      });
    }

    return (
      <button key={index} {...attrs.option}>
        <span>{dropdownLabel || label}</span>
        {isSelected ? <CheckedIcon /> : null}
      </button>
    );
  };

  const renderDropdownContent = () => {
    const attrs = {
      optionsWrapper: {
        className: css.optionsWrapper,
      },
    };

    if (renderContent) {
      return renderContent({
        options,
        selectedValue,
        handleChange,
      });
    }

    return (
      <div {...attrs.optionsWrapper}>
        {options.map((group, index) => renderOption(group, index))}
      </div>
    );
  };

  const attrs = {
    container: {
      className: css.container,
    },
    select: {
      disabled,
      className: css.select,
      type: "button" as const,
      onClick: () => {
        if (!disabled) {
          setIsDropdownOpened((prev) => !prev);
        }
      },
    },
    dropdown: {
      className: css.dropdown,
      width: dropdownWidth,
      isOpened: isDropdownOpened,
      renderContent: renderDropdownContent,
      onClose: () => {
        setIsDropdownOpened(false);
      },
    },
    selectIcon: {
      className: css.selectIcon,
    },
    chevronIcon: {
      className: getClassName(css.chevronIcon, isDropdownOpened && css.chevronIconOpened),
    },
  };

  return (
    <div {...attrs.container}>
      <Dropdown {...attrs.dropdown}>
        <button {...attrs.select}>
          {IconComponent ? <IconComponent {...attrs.selectIcon} /> : null}
          <span>{selectedOption ? selectedOption.label : placeholder}</span>
          <ChevronDownIcon {...attrs.chevronIcon} />
        </button>
      </Dropdown>
    </div>
  );
};

export default Select;
